import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type PixlieTheme = 'light' | 'dark';

const STORAGE_KEY = 'pixlie_v2_theme';

interface ThemeContextValue {
  theme: PixlieTheme;
  setTheme: (t: PixlieTheme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readInitialTheme(): PixlieTheme {
  if (typeof window === 'undefined') return 'dark';
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<PixlieTheme>(readInitialTheme);

  const setTheme = useCallback((t: PixlieTheme) => {
    setThemeState(t);
    localStorage.setItem(STORAGE_KEY, t);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [setTheme, theme]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme],
  );

  return (
    <ThemeContext.Provider value={value}>
      <div className={theme === 'dark' ? 'pixlie-app dark min-h-[100dvh]' : 'pixlie-app min-h-[100dvh]'} data-theme={theme}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
};

export function usePixlieTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('usePixlieTheme must be used within ThemeProvider');
  }
  return ctx;
}
